import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ImageComponent } from "../image/image.component";
import { MaterialModule } from '../../material/material.module';
import { PipesModule } from '../../pipes/pipes.module';
import { isValidString } from '@pineapplelab/util';
import { environment } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { FileUtils } from '../../utils/fileUtils';

@Component({
	selector: 'app-content-video-upload',
	standalone: true,
	imports: [
		ImageComponent,
		MaterialModule,
		PipesModule,
		CommonModule
	],
	templateUrl: './content-video-upload.component.html',
	styleUrl: './content-video-upload.component.scss'
})
export class ContentVideoUploadComponent implements OnChanges, OnDestroy {

	@Input() thumbnail: string | File = null;
	@Input() externalErrorMessage: string = null;
	@Input() uploaderListener: BehaviorSubject<number>;
	@Input() showError: boolean = false;

	thumbnailSrc: string = null;
	videoMaxSizeInBytes = environment.fileVideoSize;
	error: string = null;

	@Output() onVideoUploaded = new EventEmitter<{ video: File, thumbnail: File }>();
	@Output() onError = new EventEmitter<string>();
	@Output() onErrorCleared = new EventEmitter<void>();
	@Output() onVideoCancelled = new EventEmitter<void>();

	ngOnChanges(changes: SimpleChanges): void {
		const thumbnail = changes?.['thumbnail']?.currentValue;
		if (thumbnail) {
			this.resolveThumbnailDisplay(thumbnail);
		}
	}

	ngOnDestroy(): void {
		if ((this.thumbnail instanceof File) && isValidString(this.thumbnailSrc)) {
			window.URL.revokeObjectURL(this.thumbnailSrc);
		}
	}

	private resolveThumbnailDisplay(thumbnail: string | File): void {
		if (thumbnail instanceof File) {
			this.thumbnailSrc = this.getUrlForBlob(thumbnail);
		} else {
			this.thumbnailSrc = thumbnail;
		}
	}

	private getUrlForBlob(file: File): string {
		if (!file) {
			return null;
		}
		return window.URL.createObjectURL(file);
	}

	async onVideoSelected(event: Event): Promise<void> {
		const file = (event as any).target.files[0] as File;
		if (!file || !(file instanceof File)) {
			return;
		}
		if (file.size > this.videoMaxSizeInBytes) {
			this.error = 'category-creation.promotional_video_size_exceeded';
			this.onError.emit(this.error);
			return;
		}

		this.error = null;
		this.externalErrorMessage = null;
		let thumbnail: File;
		try {
			const thumbnailData = await FileUtils.getVideoThumbnail(file);
			thumbnail = thumbnailData.thumbnailFile;
			this.error = 'category-creation.promotional_video_size_exceeded';
		} catch (e) {
			this.onError.emit(this.error);
			return;
		}

		this.onVideoUploaded.emit({ video: file, thumbnail });
	}

	removeVideo(fileInput: HTMLInputElement): void {
		this.thumbnail = null;
		this.thumbnailSrc = null;
		fileInput.value = null;
		this.onVideoUploaded.emit({ video: null, thumbnail: null });
	}

	clearError(): void {
		this.uploaderListener = null;
		this.thumbnailSrc = null;
		this.thumbnail = null;
		this.onErrorCleared.emit();
	}

	cancel(fileInput?: HTMLInputElement): void {
		this.removeVideo(fileInput);
		this.onVideoCancelled.emit();
	}

}

